/**
 * SupplierJourneyOwnership (AJIP-16.02.008 · Founder Ruling S7)
 * ---------------------------------------------------------------------
 * Runtime, deterministic evaluation of:
 *   - Organisation reference validity
 *   - Representation / ownership authority
 *   - Permission set
 *
 * These are DELIBERATELY NOT embedded in SupplierJourneyDefinition (a
 * shared canonical asset). They are evaluated at activation time via
 * pluggable, framework-agnostic providers that return typed, auditable
 * outcomes.
 *
 * Providers are pure interfaces. This module never reads storage, calls
 * a network, or imports any provider-specific SDK.
 *
 * Outcome contract (frozen):
 *   {
 *     decision: 'AUTHORISED' | 'DENIED' | 'INSUFFICIENT_EVIDENCE'
 *              | 'ORGANISATION_UNKNOWN' | 'INPUT_INVALID',
 *     principalRef: string,
 *     organisationRef: string,
 *     grantedPermissions: readonly string[],
 *     deniedReasons: readonly string[],
 *     evidence: readonly object[],
 *     evaluatedAt: number
 *   }
 *
 * Fail-closed semantics:
 *   - Any missing / non-string principalRef or organisationRef →
 *     decision 'INPUT_INVALID'.
 *   - Provider throwing → decision 'INSUFFICIENT_EVIDENCE' with the
 *     provider name in deniedReasons. Never re-throws.
 */
import { SupplierOwnershipAuthorityError } from './SupplierJourneyErrors';

const OWNERSHIP_DECISIONS = Object.freeze({
  AUTHORISED: 'AUTHORISED',
  DENIED: 'DENIED',
  INSUFFICIENT_EVIDENCE: 'INSUFFICIENT_EVIDENCE',
  ORGANISATION_UNKNOWN: 'ORGANISATION_UNKNOWN',
  INPUT_INVALID: 'INPUT_INVALID',
});

const OWNERSHIP_PROVIDER_METHODS = Object.freeze([
  'lookupOrganisation',
  'evaluateAuthority',
  'listPermissions',
]);

const assertOwnershipProvider = (candidate) => {
  if (!candidate || typeof candidate !== 'object') {
    throw new SupplierOwnershipAuthorityError('OwnershipProvider: candidate must be non-null object');
  }
  for (const m of OWNERSHIP_PROVIDER_METHODS) {
    if (typeof candidate[m] !== 'function') {
      throw new SupplierOwnershipAuthorityError(`OwnershipProvider: missing method '${m}'`);
    }
  }
  return true;
};

const _freezeOutcome = (o) => Object.freeze({
  decision: o.decision,
  principalRef: o.principalRef,
  organisationRef: o.organisationRef,
  grantedPermissions: Object.freeze([...(o.grantedPermissions || [])]),
  deniedReasons: Object.freeze([...(o.deniedReasons || [])]),
  evidence: Object.freeze([...(o.evidence || [])]),
  evaluatedAt: o.evaluatedAt,
});

/**
 * Deterministically evaluate ownership at runtime. Does not read storage.
 * Uses the caller-supplied provider only. Any provider exception is
 * converted into a typed outcome (fail-closed).
 */
const evaluateOwnership = ({ principalRef, organisationRef, provider, nowFn = () => Date.now() }) => {
  const now = nowFn();
  if (typeof principalRef !== 'string' || principalRef.length === 0
    || typeof organisationRef !== 'string' || organisationRef.length === 0) {
    return _freezeOutcome({
      decision: OWNERSHIP_DECISIONS.INPUT_INVALID,
      principalRef: principalRef || '',
      organisationRef: organisationRef || '',
      grantedPermissions: [],
      deniedReasons: ['missing-required-input'],
      evidence: [],
      evaluatedAt: now,
    });
  }
  if (!provider) {
    return _freezeOutcome({
      decision: OWNERSHIP_DECISIONS.INSUFFICIENT_EVIDENCE,
      principalRef, organisationRef,
      grantedPermissions: [],
      deniedReasons: ['no-provider'],
      evidence: [], evaluatedAt: now,
    });
  }
  assertOwnershipProvider(provider);
  let organisation;
  try { organisation = provider.lookupOrganisation(organisationRef); }
  catch (_e) {
    return _freezeOutcome({
      decision: OWNERSHIP_DECISIONS.INSUFFICIENT_EVIDENCE,
      principalRef, organisationRef,
      grantedPermissions: [],
      deniedReasons: ['lookupOrganisation-threw'],
      evidence: [], evaluatedAt: now,
    });
  }
  if (organisation === null || organisation === undefined) {
    return _freezeOutcome({
      decision: OWNERSHIP_DECISIONS.ORGANISATION_UNKNOWN,
      principalRef, organisationRef,
      grantedPermissions: [],
      deniedReasons: ['organisation-not-found'],
      evidence: [], evaluatedAt: now,
    });
  }
  let authority;
  try { authority = provider.evaluateAuthority({ principalRef, organisationRef, organisation }); }
  catch (_e) {
    return _freezeOutcome({
      decision: OWNERSHIP_DECISIONS.INSUFFICIENT_EVIDENCE,
      principalRef, organisationRef,
      grantedPermissions: [],
      deniedReasons: ['evaluateAuthority-threw'],
      evidence: [], evaluatedAt: now,
    });
  }
  if (!authority || authority.authorised !== true) {
    return _freezeOutcome({
      decision: OWNERSHIP_DECISIONS.DENIED,
      principalRef, organisationRef,
      grantedPermissions: [],
      deniedReasons: [String((authority && authority.reason) || 'not-authorised')],
      evidence: authority && authority.evidence ? [...authority.evidence] : [],
      evaluatedAt: now,
    });
  }
  let permissions = [];
  try { permissions = provider.listPermissions({ principalRef, organisationRef, organisation }) || []; }
  catch (_e) { permissions = []; }
  if (!Array.isArray(permissions)) permissions = [];
  return _freezeOutcome({
    decision: OWNERSHIP_DECISIONS.AUTHORISED,
    principalRef, organisationRef,
    grantedPermissions: permissions.map(String),
    deniedReasons: [],
    evidence: authority.evidence ? [...authority.evidence] : [],
    evaluatedAt: now,
  });
};

const isAuthorised = (outcome) => outcome && outcome.decision === OWNERSHIP_DECISIONS.AUTHORISED;

export {
  OWNERSHIP_DECISIONS,
  OWNERSHIP_PROVIDER_METHODS,
  assertOwnershipProvider,
  evaluateOwnership,
  isAuthorised,
};
